import { ConnectionType, CreateBridgeDto, UpdateBridgeDto, CreateRouteDto, UpdateRouteDto } from '../types.gen';

/**
 * Represents a network pathway to a physical EdgeLink bridge device
 */
export interface BridgeRoute {
  id: string;
  namespaceId: string;
  createdAt: string;
  edgeAddressRangeCidr: string;
  mappedAddressRange?: string;
}

/**
 * Summary of an EdgeLink bridge (physical device)
 */
export interface EdgeLinkBridge {
  id: string;
  name: string;
  type: ConnectionType;
  connectionInstructions: string;
  userOptions: string | null;
  namespaceId: string;
  createdAt: string;
  updatedAt: string;
  routes: BridgeRoute[];
  currentClientVersion?: string;
  lastSeenAt?: string;
}

/**
 * Detailed view of an EdgeLink bridge including config and auth token
 */
export interface EdgeLinkBridgeDetail {
  summary: EdgeLinkBridge;
  authToken: string;
}

/**
 * Paginated response for listing bridges
 */
export interface ListEdgeLinksResponse {
  items: EdgeLinkBridge[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export type { CreateBridgeDto, UpdateBridgeDto, CreateRouteDto, UpdateRouteDto };

/**
 * Status of an EdgeLink connection
 */
export enum EdgeLinkStatus {
  UNKNOWN = 'Unknown',
  HEALTHY = 'Healthy',
  DOWN = 'Down',
  INACTIVE = 'Inactive',
}

/**
 * Individual connection to an EdgeLink bridge
 */
export interface EdgeLinkConnection {
  originIp: string;
  clientUuid: string;
  clientVersion: string;
}

/**
 * Status response for an EdgeLink bridge
 */
export interface EdgeLinkStatusResponse {
  status: EdgeLinkStatus;
  connectedAt?: string;
  disconnectedAt?: string;
  connectionType: ConnectionType;
  connections: EdgeLinkConnection[];
}

/**
 * Flattened route with bridge info for selection
 */
export interface BridgeRouteOption {
  bridgeId: string;
  bridgeName: string;
  bridgeType: ConnectionType;
  routeId: string;
  routeAddressRange: string;
  label: string;
}

/**
 * Lightweight site info for EdgeLink connected sites list
 */
export interface EdgeLinkSiteDto {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  address1?: string;
  isActive?: boolean;
  archivedAt?: string | null;
  defaultRouteNamespaceId?: string | null;
}

/**
 * Device info for EdgeLink connected sites
 */
export interface EdgeLinkDeviceDto {
  id: string;
  name: string;
  type?: string;
  isOnline?: boolean;
}

/**
 * Site with devices for EdgeLink detail view
 */
export interface EdgeLinkSiteWithDevicesDto extends EdgeLinkSiteDto {
  devices: EdgeLinkDeviceDto[];
  deviceCount: number;
}

/**
 * Connected device from the connected-assets API.
 * Devices connect via their RouteNamespaceId - this is independent of site connections.
 */
export interface ConnectedDeviceDto {
  id: string;
  name: string;
  integrationName?: string;
  routeNamespaceId?: string;
  /** The customer (company) that owns this device */
  customerId: string;
  customerName: string;
  /** The site whose DefaultRouteNamespaceId matches this device's RouteNamespaceId. Null if no matching site. */
  siteId?: string | null;
  siteName?: string | null;
}

/**
 * Connected site from the connected-assets API.
 * Sites connect via their DefaultRouteNamespaceId.
 */
export interface ConnectedSiteDto {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  isActive: boolean;
  defaultRouteNamespaceId?: string;
}

/**
 * Response from the connected-assets API endpoint.
 * Returns two independent flat lists - sites and devices.
 */

/**
 * Response from generating an EdgeLink share link
 */
export interface ShareLinkResponse {
  expiresAt: string | null;
  token: string;
}

/**
 * Public bridge status returned from share link endpoint
 */
export interface PublicBridgeStatus {
  bridgeName: string;
  status: EdgeLinkStatus;
  statusChangedAt?: string;
  expiresAt?: string;
}

export interface ConnectedAssetsResponse {
  success: boolean;
  message?: string;
  code: number;
  /** Sites with a DefaultRouteNamespaceId matching one of the bridge's routes */
  sites: ConnectedSiteDto[];
  /** All devices with a RouteNamespaceId matching one of the bridge's routes */
  devices: ConnectedDeviceDto[];
  totalSites: number;
  totalDevices: number;
}
